import { PLATFORM } from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'
import { withSpan } from '$extensionSrc/utils/tracing'

import { setStoredMachineOverlayStatus } from '../automationStageMachine'
import { wait } from '../interruptiblePromises'
import * as redditUrls from './redditUrls'

/**
 * The types of content that can be fetched from Reddit.
 */
enum RedditContentType {
  Comment = 'comment',
  Post = 'post',
}

const contentTypeToUri = {
  [RedditContentType.Comment]: 'comments/.json',
  [RedditContentType.Post]: 'submitted/.json',
}

/* Useful response headers
 * x-ratelimit-remaining: (seems to be shared by all items)
 * 96.0
 * x-ratelimit-reset: (seems to be in seconds)
 * 234
 * x-ratelimit-used:
 * 4
 */

const globalRateLimit = {
  remaining: null,
  resetSeconds: null,
  remainingGql: null,
  resetSecondsGql: null,
}

interface MeJsonData {
  username: string | undefined
  modhash: string | undefined
}

// Cache for meJson response
const meJsonCache: {
  data: MeJsonData | null
} = {
  data: null,
}

/**
 * Returns a jittered value between mean - range/2 and mean + range/2.
 *
 * @param mean - The mean value.
 * @param range - The range of the jitter.
 * @returns The jittered value.
 *
 * @example
 * getJitteredValue(1000, 500) // 750-1250
 */
function getJitteredValue(mean: number, range: number): number {
  const base = mean - range / 2
  const jitter = Math.random() * range

  return base + jitter
}

async function handleRateLimit(resetSeconds: number): Promise<void> {
  // If the rate limit is too long, maybe we should
  // exit and prompt the user to try again later
  _debug(
    `Reddit rate limit hit, waiting ${resetSeconds} seconds`,
    'redditDelete.handleRateLimit',
    'd',
  )

  await setStoredMachineOverlayStatus(PLATFORM.REDDIT, {
    title: 'Rate Limit Reached',
    body: `Waiting ${resetSeconds} seconds before continuing...`,
  })
  await wait(getJitteredValue(resetSeconds * 1000, 1000))
}

/**
 * Fetches a JSON response from Reddit.
 *
 * @param input - The input to fetch.
 * @returns The JSON response.
 */
async function redditFetch(
  input: RequestInfo | URL,
  options: RequestInit = {},
): Promise<Response> {
  return withSpan('reddit.contentUtils.redditFetch', async (span) => {
    const response = await fetch(input, options)

    const foundRateLimitRemaining = parseInt(
      response.headers.get('x-ratelimit-remaining'),
      10,
    )
    const foundRateLimitResetSeconds = parseInt(
      response.headers.get('x-ratelimit-reset'),
      10,
    )

    if (
      !Number.isNaN(foundRateLimitResetSeconds) &&
      !Number.isNaN(foundRateLimitRemaining)
    ) {
      if (response.url && response.url.includes('shreddit/graphql')) {
        globalRateLimit.remainingGql = foundRateLimitRemaining
        globalRateLimit.resetSecondsGql = foundRateLimitResetSeconds
      } else {
        globalRateLimit.remaining = foundRateLimitRemaining
        globalRateLimit.resetSeconds = foundRateLimitResetSeconds
      }

      if (foundRateLimitRemaining === 0) {
        span.addEvent('reddit.contentUtils.redditFetch.rateLimitHit', {
          rateLimitRemaining: foundRateLimitRemaining,
          rateLimitResetSeconds: foundRateLimitResetSeconds,
        })
        await handleRateLimit(foundRateLimitResetSeconds)
      }
    }

    if (!response.ok) {
      const errorBody = await response.text()
      _debug(
        `Reddit API error: ${response.status} - ${errorBody}`,
        'redditDelete.redditFetch',
        'e',
      )
      throw new Error(`Reddit API error: ${response.status}`)
    }

    return response
  })
}

/**
 * Reddit endpoint that importantly provides profile name (in case unavailable)
 * as well as modhash (required for CSRF).
 */
async function fetchMeJson(): Promise<MeJsonData> {
  // Return cached data if available
  if (meJsonCache.data) {
    _debug(
      'Using cached meJson response',
      'reddit.contentUtils.fetchMeJson',
      'd',
    )
    return meJsonCache.data
  }

  return withSpan('reddit.contentUtils.fetchMeJson', async () => {
    try {
      const response = await redditFetch(redditUrls.apiMeJsonUrl, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      })
      const payload = await response.json()

      const username = payload?.data?.name
      const modhash = payload?.data?.modhash

      if (!username || !modhash) {
        throw new Error(
          'Invalid response from Reddit: missing username or modhash',
        )
      }

      meJsonCache.data = { username, modhash }
      return meJsonCache.data
    } catch (error) {
      _debug(
        `Failed to fetch Reddit user info: ${error.message}`,
        'reddit.contentUtils.fetchMeJson',
        'e',
      )
      meJsonCache.data = null
      throw error
    }
  })
}

function getContentIdFromResponseBlob(responseBlob: any) {
  return responseBlob?.data?.name
}

async function fetchContentIdsForUser(
  username: string,
  contentType: RedditContentType,
  after: string | undefined,
): Promise<{ ids: string[]; after: string | null }> {
  const uri = contentTypeToUri[contentType]
  let url = `https://www.reddit.com/user/${username}/${uri}`
  if (after !== undefined) {
    url += `?after=${after}`
  }

  return withSpan(
    'reddit.contentUtils.fetchContentIdsForUser',
    async () => {
      const response = await redditFetch(url)
      const payload = await response.json()

      const contentBlobs = payload?.data?.children
      const newAfter = payload?.data?.after

      if (contentBlobs === undefined || newAfter === undefined) {
        _debug(response, 'reddit.contentUtils.fetchContentIdsForUser', 'd')
        throw new Error('Returned data did not match expected schema')
      }

      if (!contentBlobs) {
        return { ids: [], after: newAfter }
      }

      const contentIds = contentBlobs.map(getContentIdFromResponseBlob)

      return { ids: contentIds, after: newAfter }
    },
    {
      contentType,
      after,
    },
  )
}

/**
 * Iterator that yields content IDs for a given user and content type.
 *
 * @param username - The username of the user to fetch content IDs for.
 * @param contentType - The content type to fetch content IDs for.
 * @returns An iterator that yields content IDs until all content is fetched.
 */
async function* getContentIdIterator(
  username: string,
  contentType: RedditContentType,
) {
  let responseData = await fetchContentIdsForUser(
    username,
    contentType,
    undefined,
  )
  while (responseData.ids.length > 0) {
    for (const id_ of responseData.ids) {
      yield id_
    }

    if (responseData.after !== null) {
      responseData = await fetchContentIdsForUser(
        username,
        contentType,
        responseData.after,
      )
    } else {
      break
    }
  }
}

/**
 * Pluralizes a content type if the count is not 1.
 *
 * @param count - The count of the content type.
 * @param contentType - The content type to pluralize.
 * @returns The pluralized content type.
 *
 * @example
 * pluralize(0, RedditContentType.Post) // "0 posts"
 * pluralize(1, RedditContentType.Comment) // "1 comment"
 * pluralize(2, RedditContentType.Comment) // "2 comments"
 */
function pluralize(count: number, contentType: RedditContentType) {
  return `${count} ${contentType}${count === 1 ? '' : 's'}`
}

/**
 * Redacts a comment or post using the legacy API by editing the text to "[deleted]".
 *
 * @param contentId - The ID of the content to redact.
 */
async function redactContent(contentId: string) {
  // Throws error if modhash is not found
  const { modhash } = await fetchMeJson()

  _debug(
    `Attempting to redact ${contentId} using legacy API`,
    'reddit.contentUtils.redactContent',
    'd',
  )

  const formData = new URLSearchParams({
    api_type: 'json',
    thing_id: contentId,
    text: '[deleted]',
  })

  try {
    await redditFetch(redditUrls.apiEditUserTextUrl, {
      body: formData.toString(),
      credentials: 'include',
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        'X-Modhash': modhash,
      },
    })
  } catch (error) {
    _debug(
      `Failed to redact ${contentId}: ${error.message}`,
      'reddit.contentUtils.redactContent',
      'e',
    )
    throw error
  }
}

/**
 * Deletes a comment or post using the legacy API.
 *
 * @param contentId - The ID of the content to delete.
 */
async function deleteContent(contentId: string) {
  // Throws error if modhash is not found
  const { modhash } = await fetchMeJson()

  _debug(
    `Attempting to delete ${contentId} using legacy API`,
    'reddit.contentUtils.deleteContent',
    'd',
  )

  const formData = new URLSearchParams({
    id: contentId,
  })

  try {
    await redditFetch(redditUrls.apiDeleteUrl, {
      body: formData.toString(),
      credentials: 'include',
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        'X-Modhash': modhash,
      },
    })
  } catch (error) {
    _debug(
      `Failed to delete ${contentId}: ${error.message}`,
      'reddit.contentUtils.deleteContent',
      'e',
    )
    throw error
  }
}

export {
  deleteContent,
  fetchMeJson,
  getContentIdIterator,
  getJitteredValue,
  pluralize,
  redactContent,
  RedditContentType,
  redditFetch,
}
